import { gql, useQuery, useReactiveVar } from "@apollo/client";
import { Header, StepWizardStatus, Loader } from "components/layouts";
import lodash from "lodash";
import moment from "moment";
import { FC, useEffect, useState, useMemo } from "react";
import { useMatch } from "react-location";
import { LocationGenerics } from "router/location";
import { currentConfigVar } from "apollo/cache/config";
import DeliveryRequestDetailsContainer from "./delivery-request-details";
import FATInvoiceDetailsContainer from "./invoice-details";
import FATServiceOrderDetailsContainer from "./fat-service-order-details";
import FATDispatchCertificateDetailsContainer from "./dispatch-certificate";
import MaterialsDetailsContainer from "./materials-details";

const GET_METER_DELIVERY_REQUEST = gql`
  query GetMeterDeliveryRequestServiceRequest($id: ID!) {
    deliveryRequest: getMeterDeliveryServiceRequest(id: $id) {
      _id
      code
      createdAt
      createdBy {
        firstName
        lastName
        phoneNumber
      }
      confirmedBy {
        firstName
        lastName
        phoneNumber
      }
      confirmedAt
      rejectedBy {
        firstName
        lastName
        phoneNumber
      }
      request {
        contractNumber    
        contractTitle
        proposedStartDate
        estimatedProductionCompletionDate
        testSets {
          meterBrand {
            _id
            code
            name
          }
          meterModel  {
            _id
            code
            name
          }
          meterPhase
          content {
            meterNumber
            stsNumber
          }
        }
        testAtOwnFacility  
        singlePhaseMeterEnclosures
        threePhaseMeterEnclosures
      }
      response {
        FATServiceOrders {
          _id  
        }
        FATInvoice {
          _id
        }
        dispatchDetails {
          FATServiceOrder {
            code
          }
          meterBrand {
            name
          }
          meterModel {
            name
          }
          meterPhase
          isMMSCompliant
          setCount
          permittedDistricts {
            _id
            name
            regionName
          }
        }
        materials {
          _id
        }
      }
      meterContractorCode
      meterContractor {
        _id
        name
        ghanaPostAddress
        address
        testingFacility {
          name
          address
        }
        documentation {
          approvedTechnicalDocument {
            url
            uploadedAt
          }
          calibrationCertificate {
            url
            uploadedAt
          }
          typeTestReport {
            url
            uploadedAt
          }
          ISO14001 {
            url
            uploadedAt
          }
          ISO45001 {
            url
            uploadedAt
          }
          ISO9001 {
            url
            uploadedAt
          }
          schematicWiringDrawing {
            url
            uploadedAt
          }
          installationOperationalAndMaintenanceManuals {
            url
            uploadedAt
          }
          detailedPartListAndMaterialsOfConstruction {
            url
            uploadedAt
          }
        }
      }
      status
      dispatchGeneratedAt
    }
  }
`;

const serviceRequestSteps = [
  {
    name: "Delivery Request",
    description: "",
    Component: DeliveryRequestDetailsContainer,
  },
  {
    name: "FAT Invoice",
    description: "",
    Component: FATInvoiceDetailsContainer,
  },
  {
    name: "FAT Service Orders",
    description: "",
    Component: FATServiceOrderDetailsContainer,
  },
  {
    name: "Dispatch Clearance Certificate",
    description: "",
    Component: FATDispatchCertificateDetailsContainer,
  },
  {
    name: "Complementary Materials",
    description: "",
    Component: MaterialsDetailsContainer,
  },
];



export const MeterDeliveryServiceRequestPage: FC = () => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  const [step, setStep] = useState(0);
  const [lastStep, setLastStep] = useState(0);

  const { params } = useMatch<LocationGenerics>();

  const { data, loading } = useQuery(GET_METER_DELIVERY_REQUEST, {
    variables: {
      id: params?.deliveryRequest,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !params?.deliveryRequest,
  });

  const filteredFormSteps = useMemo(
    () => serviceRequestSteps.filter((_step) => true),
    []
  );

  const steps = useMemo(
    () => [...filteredFormSteps].map((rawStep, href) => ({ ...rawStep, href })),
    [filteredFormSteps]
  );

  useEffect(() => {
    switch (data?.deliveryRequest?.status) {
      case "ApplicationCreated":
      case "ApplicationConfirmed":
      case "ApplicationRejected": {
        setStep(0);
        setLastStep(0);
        break;
      }
      case "InvoiceGenerated":
      case "InvoicePaid": {
        setStep(1);
        setLastStep(1);
        break;
      }
      case "ServiceOrderInitiated": {
        setStep(2);
        setLastStep(2);
        break;
      }
      case "DispatchClearanceGenerated": {
        setStep(3);
        setLastStep(3);
        break;
      }
      case "ComplementaryMaterialsGenerated":
      case "ComplementaryMaterialsDistributed":
      case "ComplementaryMaterialsQueued":{
        setStep(4);
        setLastStep(4);
        break;
      }
      default:
        setStep(0);
        setLastStep(0);
        break;
    }
  }, [data?.deliveryRequest?.status, data?.deliveryRequest?._id]);

  const renderView = () => {
    const { Component } = steps[step || 0];
    return (
      <Component
        deliveryRequest={data?.deliveryRequest}
        id={lodash.get(data?.deliveryRequest, "_id")}
        code={lodash.get(data?.deliveryRequest, "code")}
        status={lodash.get(data?.deliveryRequest, "status")}
        createdAt={lodash.get(data?.deliveryRequest, "createdAt")}
        updatedAt={lodash.get(data?.deliveryRequest, "updatedAt")}
      />
    );
  };

  return (
    <main className='flex-1 flex flex-col overflow-hidden h-screen bg-gray-50'>
      <Header />
      <div className='flex flex-1 overflow-hidden'>
        {loading ? (
          <Loader />
        ) : (
          <div className='bg-gray-100 shadow-xl flex-1 flex p-8 overflow-hidden relative'>
            <div className='w-1/4 flex flex-col space-y-6 overflow-y-auto no-scrollbar'>
              <div className='p-6 border border-dashed border-gray-300 rounded grid grid-cols-2 gap-6 mr-12'>
                <div>
                  <span className='block text-md font-light text-gray-700'>
                    Request Code
                  </span>
                  <div className='mt-1 block w-full text-md'>
                    {data?.deliveryRequest?.code || "N/A"}
                  </div>
                </div>
                <div className=''>
                  <span className='block text-md font-light text-gray-700'>
                    Request Date
                  </span>
                  <div className='mt-1 block w-full text-md'>
                    {data?.deliveryRequest?.createdAt
                      ? moment(data?.deliveryRequest?.createdAt).format(
                        dateFormat
                      )
                      : "N/A"}
                  </div>
                </div>
              </div>
              <StepWizardStatus
                steps={steps}
                step={step}
                setStep={setStep}
                lastStep={lastStep}
              />
            </div>
            <div className='flex flex-1 overflow-hidden bg-white rounded-lg'>
              {renderView()}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default MeterDeliveryServiceRequestPage;
